import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { extractErrorMessage } from '../../utils/error-handler';
import { smsTemplateService } from '../../services/sms-template.service';
import type {
  SmsTemplate, SmsTemplateApproval, SmsTemplateLookup,
} from '../../types/sms-template.types';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';

/**
 * DLT templates for the SMS gateway.
 *
 * Indian transactional SMS may only carry wording registered with the telecom
 * operator in advance. An unregistered send is accepted by the API and silently
 * dropped by the operator, so this screen's real job is making the STATE of a
 * template visible — approved or not — rather than authoring text freely.
 *
 * The two-step nobody can skip: register the wording on the operator's DLT portal,
 * wait for them to issue an id, then record that id here. The id never arrives
 * through the API, which is why it is editable and why a template with one counts
 * as approved.
 */

/** Placeholder syntax authors write, matching every other channel in the system. */
const VARIABLE_RE = /\{\{?\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}?\}/g;
/** What the operator's portal produces, pasted in as-is. Positional, unnamed. */
const PORTAL_RE = /\{#\s*[A-Za-z0-9_ ]*?\s*(?:#\}|\}\})/g;

/**
 * The slots a body declares, mirroring the backend so the author sees the same
 * answer before saving as the server records after. Kept in step deliberately: a
 * preview that disagreed with what is sent would be worse than no preview.
 */
const detectVariables = (body: string): string[] => {
  let index = 0;
  const converted = (body || '')
    .replace(PORTAL_RE, () => `##VAR${++index}##`)
    .replace(VARIABLE_RE, (_m, name) => `##${String(name).replace(/[^A-Za-z0-9_]/g, '_').toUpperCase()}##`);
  const out: string[] = [];
  for (const m of converted.matchAll(/##\s*([A-Za-z0-9_]+)\s*##/g)) {
    if (!out.includes(m[1])) out.push(m[1]);
  }
  return out;
};

const APPROVAL_SEVERITY: Record<SmsTemplateApproval, 'success' | 'warning' | 'danger' | 'info'> = {
  APPROVED: 'success', PENDING: 'warning', REJECTED: 'danger', UNKNOWN: 'info',
};

const ApprovalTag = ({ status }: { status?: SmsTemplateApproval }) => {
  const value = status || 'UNKNOWN';
  return <Tag value={value} severity={APPROVAL_SEVERITY[value]} />;
};

const EMPTY = { name: '', body: '', senderId: '', dltTemplateId: '' };

const SmsTemplateList = ({ embedded = false }: { embedded?: boolean }) => {
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.SMS_TEMPLATES);
  const canUpdate = useCanUpdate(JOB_NAMES.SMS_TEMPLATES);

  const [rows, setRows] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // Paged in the browser, not on the server: the endpoint returns a project's whole
  // template set, which is dozens of rows at most — a template is registered by
  // hand and reviewed by a telecom operator, so the count cannot run away.
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The edit dialog holds BOTH ids. The gateway's id addresses the send; the
  // operator's DLT id is what the gateway checks it against, and a row can be
  // missing either for reasons that have nothing to do with its wording.
  const [dltRow, setDltRow] = useState<SmsTemplate | null>(null);
  const [dltValue, setDltValue] = useState('');
  const [gatewayValue, setGatewayValue] = useState('');
  const [lookup, setLookup] = useState<SmsTemplateLookup | null>(null);
  const [lookupError, setLookupError] = useState('');
  const [lookingUp, setLookingUp] = useState(false);

  const openEdit = (r: SmsTemplate) => {
    setDltRow(r);
    setDltValue(r.dltTemplateId || '');
    setGatewayValue(r.providerTemplateId || '');
    setLookup(null);
    setLookupError('');
  };

  const notify = (severity: 'success' | 'error' | 'warn', summary: string, detail?: string) =>
    toast.current?.show({ severity, summary, detail, life: severity === 'error' ? 8000 : 4000 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await smsTemplateService.list());
    } catch (e) {
      notify('error', 'Could not load templates', extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /* There is no "pull every template": MSG91 publishes no endpoint that lists
     an account's templates. What it does answer is the versions of ONE template
     id, which every template registered here has — so this asks about the ones
     still awaiting an operator id and settles each from the answer. */
  const [checking, setChecking] = useState(false);
  const checkApprovals = async () => {
    setChecking(true);
    try {
      const r = await smsTemplateService.checkApprovals();
      notify(r.approved ? 'success' : 'warn', 'Checked',
        `${r.checked} checked · ${r.approved} approved · ${r.rejected} rejected · `
        + `${r.stillPending} still pending`
        + (r.notRegistered
          ? ` · ${r.notRegistered} never got an id from the gateway — re-register those`
          : ''));
      await load();
    } catch (e) {
      notify('error', 'Could not check approvals', extractErrorMessage(e));
    } finally {
      setChecking(false);
    }
  };

  const openCreate = () => { setForm({ ...EMPTY }); setErrors({}); setCreateOpen(true); };

  const save = async () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'A template name is required';
    if (!form.body.trim()) next.body = 'The message wording is required';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      await smsTemplateService.create({
        name: form.name.trim(),
        body: form.body,
        senderId: form.senderId.trim() || undefined,
        dltTemplateId: form.dltTemplateId.trim() || undefined,
      });
      notify('success', 'Template registered',
        form.dltTemplateId.trim() ? 'Approved and ready to use.'
                                  : 'Pending until a DLT template ID is added.');
      setCreateOpen(false);
      await load();
    } catch (e) {
      notify('error', 'Could not register the template', extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* Check the gateway id before it is saved. A lookup that fails is the useful
     answer — it means this id addresses nothing on this account — so it is shown
     in the dialog rather than raised as a toast that closes over the form. */
  const fetchDetails = async () => {
    const id = gatewayValue.trim();
    if (!id) {
      setLookup(null);
      setLookupError('Enter the gateway template id first.');
      return;
    }
    setLookingUp(true);
    setLookup(null);
    setLookupError('');
    try {
      setLookup(await smsTemplateService.lookup(id));
    } catch (e) {
      setLookupError(extractErrorMessage(e)
        || `The gateway has no template with id "${id}" on this project's account.`);
    } finally {
      setLookingUp(false);
    }
  };

  /* Offered only when the gateway disagrees with the row: copying values that
     already match would just be a button that appears to do nothing. */
  const applyFetched = () => {
    if (!lookup) return;
    if (lookup.dltTemplateId) setDltValue(lookup.dltTemplateId);
  };

  const saveDlt = async () => {
    if (!dltRow) return;
    setSaving(true);
    try {
      await smsTemplateService.update(dltRow._id, {
        dltTemplateId: dltValue.trim(),
        providerTemplateId: gatewayValue.trim(),
      });
      const gatewayChanged = gatewayValue.trim() !== (dltRow.providerTemplateId || '');
      notify('success',
        dltValue.trim() ? 'Saved — DLT id recorded, template approved'
                        : 'Saved — no DLT id, template is pending',
        gatewayChanged
          ? (gatewayValue.trim() ? 'Gateway template id updated.'
                                 : 'Gateway template id cleared — this template cannot be '
                                   + 'sent with until one is recorded.')
          : undefined);
      setDltRow(null);
      await load();
    } catch (e) {
      notify('error', 'Could not update the template', extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const previewVariables = useMemo(() => detectVariables(form.body), [form.body]);

  const sorted = useMemo(() => {
    if (!sortField || !sortOrder) return rows;
    return [...rows].sort((a, b) => {
      const av = String((a as unknown as Record<string, unknown>)[sortField] ?? '');
      const bv = String((b as unknown as Record<string, unknown>)[sortField] ?? '');
      return av.localeCompare(bv) * sortOrder;
    });
  }, [rows, sortField, sortOrder]);

  const pageRows = useMemo(
    () => sorted.slice(first, first + pageSize), [sorted, first, pageSize]);

  const columns: SaasColumn<SmsTemplate>[] = useMemo(() => [
    {
      field: 'templateName', header: 'Template', sortable: true, hideable: false,
      body: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.templateName}</span>
          <span className="text-xs text-ink-muted line-clamp-1">{r.body}</span>
        </div>
      ),
    },
    {
      field: 'approvalStatus', header: 'Approval', align: 'center',
      body: (r) => <ApprovalTag status={r.approvalStatus} />,
    },
    {
      field: 'dltTemplateId', header: 'DLT Id',
      body: (r) => r.dltTemplateId
        ? <span className="font-mono text-xs">{r.dltTemplateId}</span>
        : <span className="text-xs text-amber-600 dark:text-amber-400">not yet issued</span>,
    },
    {
      field: 'variables', header: 'Variables',
      body: (r) => (r.variables?.length
        ? <span className="font-mono text-xs">{r.variables.join(', ')}</span>
        : <span className="text-xs text-gray-400">none</span>),
    },
    {
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          {/* Every row here belongs to this project, so every row is editable. */}
          {canUpdate && (
            <button aria-label="Edit"
              className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={() => openEdit(r)}
              data-pr-tooltip="Edit template ids"
            >
              <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
            </button>
          )}
        </div>
      ),
    },
  ], [canUpdate]);

  return (
    <PermissionGuard jobName={JOB_NAMES.SMS_TEMPLATES}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-SmsTemplates-List">
        {!embedded && (
          <PageHeader icon="pi pi-comment" title="SMS Templates"
                      subtitle="DLT-registered wording for the SMS gateway" />
        )}

        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-3">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <i className="pi pi-info-circle mr-2" />
            Indian transactional SMS may only carry wording registered with the telecom
            operator. Register on the DLT portal first, then record the id it issues here —
            a template without one is <strong>not</strong> sent, and the operator drops it
            without reporting an error.
          </p>
        </div>

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            fillHeight={!embedded}
            tableId="sms-templates" columns={columns} data={pageRows} loading={loading} dataKey="_id"
            emptyMessage="No SMS templates yet" testId="SmsTemplatesList"
            first={first} rows={pageSize} totalRecords={sorted.length}
            onPage={(e) => { setFirst(e.first); setPageSize(e.rows); }}
            rowsPerPageOptions={[10, 25, 50]}
            sortField={sortField} sortOrder={sortOrder}
            onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder); }}
            onFilter={() => { /* no per-column filters: the set is small enough to read whole */ }}
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {rows.length} {rows.length === 1 ? 'template' : 'templates'}
                </span>
                <RefreshButton onClick={load} loading={loading} />
                {canUpdate && (
                  <Button label="Check approvals" icon="pi pi-sync" size="small" outlined
                          onClick={checkApprovals} loading={checking}
                          tooltip="Ask the provider what became of templates awaiting a DLT id" />
                )}
                {canCreate && (
                  <Button label="Register Template" icon="pi pi-plus" size="small" onClick={openCreate} />
                )}
              </>
            }
          />
        </div>

        {/* ---------------------------------------------------------- create */}
        <Dialog
          header="Register SMS template" visible={createOpen}
          onHide={() => !saving && setCreateOpen(false)}
          style={{ width: '560px', maxWidth: '96vw' }}
          footer={
            <div className="flex justify-end gap-2">
              <Button label="Cancel" severity="secondary" outlined
                      onClick={() => setCreateOpen(false)} disabled={saving} />
              <Button label="Register" icon="pi pi-check" onClick={save} loading={saving} />
            </div>
          }
        >
          <div className="flex flex-col gap-4 pt-2">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Template name *</label>
              <InputText value={form.name} placeholder="e.g. login_otp"
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         className={errors.name ? 'p-invalid' : ''} />
              {errors.name
                ? <small className="text-red-600">{errors.name}</small>
                : <small className="text-gray-500">Used to pick this template in a notification.</small>}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Message *</label>
              <InputTextarea rows={5} value={form.body} autoResize
                             onChange={(e) => setForm({ ...form, body: e.target.value })}
                             className={errors.body ? 'p-invalid' : ''}
                             placeholder={'Your OTP is {otp}. Valid for {expiry} minutes.'} />
              {errors.body
                ? <small className="text-red-600">{errors.body}</small>
                : <small className="text-gray-500">
                    Put values that change in braces, e.g. <code>{'{otp}'}</code> — or paste
                    your approved DLT template as it is.
                  </small>}
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span className="text-sm text-gray-500">Variables:</span>
                {previewVariables.length
                  ? previewVariables.map((v) => <Tag key={v} value={v} severity="info" />)
                  : <span className="text-sm text-gray-400">none</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">Sender ID</label>
                <InputText value={form.senderId} placeholder="Optional"
                           onChange={(e) => setForm({ ...form, senderId: e.target.value })} />
                <small className="text-gray-500">Blank uses the sms config </small>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">DLT template ID</label>
                <InputText value={form.dltTemplateId} placeholder="Optional"
                           onChange={(e) => setForm({ ...form, dltTemplateId: e.target.value })} />
                <small className="text-gray-500">For India: Add correct Template ID that is approved on DLT platform.</small>
              </div>
            </div>
          </div>
        </Dialog>

        {/* ------------------------------------------------------- record id */}
        <Dialog
          header="Edit template" visible={!!dltRow} onHide={() => !saving && setDltRow(null)}
          style={{ width: '540px', maxWidth: '96vw' }}
          footer={
            <div className="flex justify-end gap-2">
              <Button label="Cancel" severity="secondary" outlined
                      onClick={() => setDltRow(null)} disabled={saving} />
              <Button label="Save" onClick={saveDlt} loading={saving} />
            </div>
          }
        >
          <div className="flex flex-col gap-3 pt-2">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Template <strong>{dltRow?.templateName}</strong>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Gateway Template Id</label>
              <div className="flex gap-2">
                <InputText value={gatewayValue} className="font-mono flex-1"
                           onChange={(e) => {
                             setGatewayValue(e.target.value);
                             setLookup(null);
                             setLookupError('');
                           }}
                           placeholder="e.g. 669a29fbd6fc053066739b42" />
                <Button label="Fetch details" outlined severity="secondary"
                        onClick={fetchDetails} loading={lookingUp}
                        disabled={saving} />
              </div>
              <small className="text-gray-500">
                The id the provider assigned. It is what a send is addressed with, so a
                template without one cannot be sent — record it from the provider's
                console if registration did not capture it.
              </small>

              {lookupError && (
                <div className="mt-1 rounded border border-red-200 bg-red-50 p-2 text-xs
                                text-red-700 dark:border-red-800 dark:bg-red-900/20
                                dark:text-red-300">
                  <i className="pi pi-times-circle mr-1" />
                  {lookupError}
                </div>
              )}

              {lookup && (
                <div className="mt-1 rounded border border-green-200 bg-green-50 p-2 text-xs
                                dark:border-green-800 dark:bg-green-900/20">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="pi pi-check-circle text-green-600" />
                    <span className="font-medium">Found at the gateway</span>
                    <ApprovalTag status={lookup.approvalStatus} />
                  </div>
                  <div className="text-gray-700 dark:text-gray-300">
                    <div><strong>Name:</strong> {lookup.templateName || '—'}</div>
                    <div><strong>Sender:</strong> {lookup.senderId || '—'}</div>
                    <div>
                      <strong>DLT id:</strong>{' '}
                      {lookup.dltTemplateId
                        ? <span className="font-mono">{lookup.dltTemplateId}</span>
                        : <span className="text-amber-600">not issued yet</span>}
                    </div>
                    {lookup.approvalReason && (
                      <div><strong>Operator said:</strong> {lookup.approvalReason}</div>
                    )}
                    {lookup.body && (
                      <div className="mt-1 whitespace-pre-wrap font-mono
                                      text-gray-600 dark:text-gray-400">
                        {lookup.body}
                      </div>
                    )}
                  </div>
                  {/* The wording stays read-only — DLT needs a fresh registration for
                      any change to it — so only the DLT id can be copied across. */}
                  {!!lookup.dltTemplateId && lookup.dltTemplateId !== dltValue.trim() && (
                    <Button label="Use this DLT id" link className="p-0 mt-1 text-xs"
                            onClick={applyFetched} />
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">DLT Template Id</label>
              <InputText value={dltValue} onChange={(e) => setDltValue(e.target.value)}
                         className="font-mono" placeholder="e.g. 1707161234567890123" />
              <small className="text-gray-500">
                Recording an id marks the template approved — the operator does not issue one
                for wording it refused. Clearing it returns the template to pending.
              </small>
            </div>
            <div className="bg-canvas dark:bg-gray-900/40 rounded p-2 text-xs whitespace-pre-wrap font-mono">
              {dltRow?.body}
            </div>
            <small className="text-gray-500">
              The wording cannot be edited here: DLT requires a fresh registration for any
              change to the text, so an edit would leave a record that looks current and
              sends nothing.
            </small>
          </div>
        </Dialog>
      </div>
    </PermissionGuard>
  );
};

export default SmsTemplateList;
